import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { wrapPromise } from "go-core";
import Header from "go-app/components/Header";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import ReportData from "go-report/reports";
import { reportParamsConverter } from "go-report/reports/services/report-service";
import { ReportConfig } from "go-report/reports/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { partnerApi } from "../../../../../../../../services/Api/Partner/partnerApi";
import { selectPartner } from "../../../../../../../../services/partner/selectors";
import ReportPartnerUtils from "../../../../../Admin/services/ReportPartnerUtils";

const listName = "PARTNER_PORTAL_REPORT";
const resource_type = "PARTNER_PORTAL_REPORT";

interface State {
	resource?: Record<string, any>;
}

const Report = ({ resource }: State) => {
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);
	const partner = useSelector(selectPartner);
	const configId = "partner_report";

	if (!resource) return null;
	const data = resource.read();

	let config = {
		reportConfigId: configId,
		aggregatePrefix: "summary",
		selectedColumns: ReportPartnerUtils.getSelectedColumns(configId),
		groups: ReportPartnerUtils.getGroups(t, [
			"DATE",
			"NAME",
			"CLIENT_NAME",
			"BILLED_PERIOD",
			"PROFORMA_ID",
			"SUBSCRIPTION_ID",
			"PAID_AT",
			"TRANSACTION_DATE",
			"CLIENT_COMPANY_NAME",
			"CLIENT_TAX_ID_NO",
			"FAMILY_ID",
			"SUB_PARTNER",
			"SUB_PARTNER_TAX_ID_NO",
			"PROVISION_PROVISION",
		]),
		showPercent: true,
		columns: ReportPartnerUtils.getColumns(t),
		selectedGroups: ["DATE"],
		groupsInfo: ReportPartnerUtils.getGroupsInfo(t),
		filters: [
			...ReportPartnerUtils.getFilters(t, [
				"name",
				"date",
				"transaction_at",
				"client_name",
				"client_company_name",
				"price_paid",
				"billed_period",
				"proforma_id",
				"paid_at",
				"client_tax_id_no",
				"provision_value",
				"subscription_id",
				"family_id",
				"sub_partner",
				"sub_partner_tax_id_no",
				"provision_provision",
			]),
		],
		segments: [
			{
				id: "all",
				name: `${t("common.word.all", { ns: "lib" })}`,
				slug: "all",
				columns: ReportPartnerUtils.getSelectedColumns(configId),
				filters: [],
			},
		],
		fetch: async (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return Promise.all([
				partnerApi.getPartnerReport(partner?.id, reportParamsConverter(params), {
					cancelToken: sourceToken?.token,
				}),
			]);
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentContextValue.save(listName, resource_type, segment);
		},
		exportConfig: {
			title: t("modules.provision_report.header.title"),
			filename: t("modules.provision_report.field.export_config.filename"),
		},
		doNotUseDefaultDateRangeInFilters: true,
	} as ReportConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

const PartnerPreviewReportPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(
			`${t("modules.provision_report.header.title")} | ${t("modules.partner.field.partner_panel.title")}`
		);
		setResource(wrapPromise(segmentContextValue.get(listName, resource_type)));
	}, []);

	return (
		<div className={"content"}>
			{!isMobile && <Header title={t("modules.provision_report.header.title")} />}
			<Suspense fallback={<LoadingContainer />}>
				<Report resource={resource} />
			</Suspense>
		</div>
	);
};
export default PartnerPreviewReportPage;
